import { useEffect, useState } from "react";
import { Box, Card, CardContent, LinearProgress, Typography } from "@mui/material";
import apiService from "../../../service/Api";

interface FundraiserSummary {
  id: number;
  slug: string;
  title: string;
  fundraiser_type: string;
  goal_amount: string | null;
  amount_raised: string;
  progress_pct: number | null;
  donor_count: number;
  currency: string;
  share_path: string;
}

/**
 * Reuses the same by-stream lookup DonationTab.tsx already relies on
 * (chema/views.py's by_stream resolves through booking->deceased, so this
 * shows the identical fundraiser as the portal and MMS /chema listing).
 */
export default function FundraiserCard({ streamId }: { streamId: string | number }) {
  const [fundraiser, setFundraiser] = useState<FundraiserSummary | null>(null);

  useEffect(() => {
    let active = true;
    if (!streamId) return;
    apiService
      .get<FundraiserSummary>(`/api/chema/campaigns/by-stream/${streamId}/`)
      .then((res) => active && setFundraiser(res.data))
      .catch(() => active && setFundraiser(null));
    return () => {
      active = false;
    };
  }, [streamId]);

  if (!fundraiser) return null;

  const currencySymbol = fundraiser.currency === "USD" ? "$" : `${fundraiser.currency} `;
  const hasGoal = fundraiser.fundraiser_type === "goal" && !!fundraiser.goal_amount;

  return (
    <Card sx={{ boxShadow: 0, borderRadius: "20px", border: "1px solid", borderColor: "divider" }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          Chema Fundraiser
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {fundraiser.title}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {currencySymbol}
            {Number(fundraiser.amount_raised).toLocaleString()} raised
          </Typography>
          {hasGoal && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              of {currencySymbol}
              {Number(fundraiser.goal_amount).toLocaleString()} goal
            </Typography>
          )}
        </Box>

        {hasGoal && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, fundraiser.progress_pct ?? 0)}
            sx={{ borderRadius: 4, height: 8, mb: 1 }}
          />
        )}

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {fundraiser.donor_count} donor{fundraiser.donor_count === 1 ? "" : "s"}
        </Typography>

        <Box sx={{ mt: 1.5 }}>
          <a
            href={`/chema/insights/${fundraiser.id}`}
            style={{ fontSize: "0.85rem", fontWeight: 600 }}
          >
            View fundraiser details
          </a>
        </Box>
      </CardContent>
    </Card>
  );
}
